/**
 * Formats indicator data points into Chart.js-compatible objects.
 *
 * @param {string} label - The dataset label (e.g., indicator name).
 * @param {Array<{date: Date, value: number}>} points - Array of data points containing a date and its corresponding value.
 * @returns {{label: string, data: Array<{x: string, y: number}>}} 
 *          An object ready for Chart.js, where each point is formatted with ISO date strings and numeric values.
 *
 * @description
 * Converts a list of raw indicator points into a `{ label, data }` format expected by Chart.js.  
 * Each point’s `sessionDate` is converted to an ISO string to ensure correct X-axis scaling.
 */
export const indicatorFormatter = (label, points) => {
    return ({
        label, data: points.map((point) => ({ x: point.date.toISOString(), y: point.value }))
    })
}

/**
 * Formats a single instrument's dataset for single-axis plotting.
 *
 * @param {string} type - The key name of the numeric property to plot (e.g., "yield", "price").
 * @param {Array<{sessionDate: Date, [key: string]: number}>} points - List of data points for the instrument.
 * @param {string} instrumentId - Unique identifier of the instrument.
 * @param {string} instrumentName - Display name of the instrument.
 * @returns {{id: string, label: string, data: Array<{x: string, y: number}>}}
 *          A formatted dataset ready for Chart.js with consistent ISO date formatting.
 *
 * @description
 * Extracts and formats time-series data for a single instrument into Chart.js format.  
 * Each point’s `sessionDate` is converted to an ISO string to ensure correct X-axis scaling.
 */
export const singleAxisFormatter = (type, points, instrumentId, instrumentName) => {
    return ({
        id: instrumentId, label: instrumentName, data: points.map((point) => ({ x: point.sessionDate.toISOString(), y: point[type] }))
    })
}

/**
 * Formats multiple instruments' datasets for multi-axis plotting.
 *
 * @param {string} type - The key name of the numeric property to plot (e.g., "yield", "price").
 * @param {Array<{id: string, name: string, points: Array<{sessionDate: Date, [key: string]: number}>}>} hisotricDatasets
 *        Array of instrument datasets, each containing an ID, label, and time-series points.
 * @returns {Array<{id: string, label: string, data: Array<{x: string, y: number}>}>}
 *          A list of Chart.js-compatible datasets, one per instrument.
 *
 * @description
 * Maps each dataset to a consistent structure for Chart.js rendering.  
 * Each point’s `sessionDate` is converted to an ISO string to ensure correct X-axis scaling.
 */
export const multiAxisFormatter = (type, hisotricDatasets) => {
    return hisotricDatasets.map(dataset => ({
        id: dataset.id, label: dataset.name, data: dataset.points.map((point) => ({ x: point.sessionDate.toISOString(), y: point[type] }))
    }));
}

